import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { NavigationExtras, Router } from '@angular/router';
import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';
import { Preferences } from '@capacitor/preferences';
import { ModalController } from '@ionic/angular';
import { deleteField, doc, getFirestore, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ComponentsService } from '../components/components.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    private initialized: boolean = false;

    public isAccountAvailable: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private authenticatedSubject = new BehaviorSubject<boolean>(false);

    get isAuthenticated(): Observable<boolean> {
        return this.authenticatedSubject.asObservable();
    }

    private accountUnsub?: () => void;

    private readonly _countState$ = new BehaviorSubject<{
        hasActiveCount: boolean;
        counting_key: string | null;
    }>({
        hasActiveCount: false,
        counting_key: null
    });

    account_key: any;
    device_key: any;
    location_key: any;
    area_key: any;
    mode: any;
    device: any;
    license: any;
    location: any;
    area: any;
    account: any;
    uid: any;
    user: any;
    logged_token: boolean = false;
    user$ = this.angularAuth.authState;
    private destroy$ = new Subject<void>();

    constructor(
        public database: AngularFirestore,
        private router: Router,
        public db: AngularFirestore,
        private angularAuth: AngularFireAuth,
        public components: ComponentsService,
        private http: HttpClient,
        private modalCtrl: ModalController,
    ) {
    }

    readonly countState$ = this._countState$.asObservable();

    async waitForAccount(): Promise<void> {
        if (this.account) return; // ya está
        await firstValueFrom(this.isAccountAvailable.pipe(
            filter(v => v === true),
            take(1)
        ));
    }


    async init(): Promise<void> {
        return new Promise(async (resolve) => {
            if (this.initialized) return;

            const status = await Network.getStatus().catch(() => ({ connected: false }));
            if (!status.connected) {
                setTimeout(() => this.init(), 5000);
                return;
            }

            this.angularAuth.authState.subscribe(async (firebaseUser) => {
                if (firebaseUser) {

                    this.uid = firebaseUser.uid;
                    await this.checkUser();
                    this.authenticatedSubject.next(true);

                    await this.loadToken().then(() => {
                        this.initialized = true;
                        resolve();
                    })
                } else {
                    this.authenticatedSubject.next(false);
                    this.initialized = true;
                    resolve();
                }
            });
        });
    }

    async loadToken() {
        const keys = ['type', 'account_key', 'device_key', 'location_key', 'area_key'];
        const stored = await Promise.all(keys.map(key => Preferences.get({ key })));
        const [type, acc, dev, loc, area] = stored.map(d => d.value);

        if (type === 'abc' && acc && dev) {
            await this.setAbcContext({
                account_key: acc,
                device_key: dev,
                location_key: loc || null,
                area_key: area || null,
            });
            this.checkDevice();
            this.checkLocation();
            this.checkArea();
            this.authenticatedSubject.next(true);
            return;
        }

        if (type === 'admin' && !acc) {
            this.mode = 'admin';
            this.account_key = null;
            this.authenticatedSubject.next(true); // deja pasar al /accounts
            return;
        }

        if (type === 'admin' && acc) {
            await this.setAdminContext(acc);
            this.authenticatedSubject.next(true);
            return;
        }

        // Sin contexto válido
        this.authenticatedSubject.next(false);
    }

    public async setAdminContext(account_key: string) {
        this.mode = 'admin';
        this.account_key = account_key;
        this.device_key = this.location_key = this.area_key = null;

        await Preferences.set({ key: 'type', value: 'admin' });
        await Preferences.set({ key: 'account_key', value: account_key });
        await Preferences.remove({ key: 'device_key' });
        await Preferences.remove({ key: 'location_key' });
        await Preferences.remove({ key: 'area_key' });

        this.attachAccountListener(account_key);

    }

    normalizeUser(user: any) {
        if (!user) return null;

        return {
            $key: user?.$key || user?.uid || null,
            uid: user?.uid || user?.$key || null,
            name: user?.name || '',
            lastname: user?.lastname || '',
            email: user?.email || '',
            type: user?.type || null,
        };
    }

    get user_resume() {
        return this.normalizeUser(this.user);
    }

    private async setAbcContext(opts: { account_key: string; device_key: string; location_key?: string | null; area_key?: string | null; }) {
        this.mode = 'abc';
        this.account_key = opts.account_key;
        this.device_key = opts.device_key;
        this.location_key = opts.location_key ?? null;
        this.area_key = opts.area_key ?? null;

        await Preferences.set({ key: 'type', value: 'abc' });
        await Preferences.set({ key: 'account_key', value: opts.account_key });
        await Preferences.set({ key: 'device_key', value: opts.device_key });
        await Preferences.set({ key: 'location_key', value: this.location_key ?? '' });
        await Preferences.set({ key: 'area_key', value: this.area_key ?? '' });

        this.attachAccountListener(opts.account_key);

    }

    private attachAccountListener(account_id: string) {
        // Limpia anterior
        if (this.accountUnsub) { this.accountUnsub(); this.accountUnsub = undefined; }

        this.account = null;
        this.isAccountAvailable.next(false);

        const docRef = this.db.collection('accounts_abc').doc(account_id).ref;

        this.accountUnsub = docRef.onSnapshot(async (snapshot: any) => {
            if (!snapshot.exists) {
                this.account = null;
                this.isAccountAvailable.next(false);
                return;
            }

            this.account = { $key: snapshot.id, ...snapshot.data() };

            if (this.mode == 'admin') {
                try {
                    const userDoc: any = await this.db
                        .collection(`accounts_abc/${account_id}/users`)
                        .doc(this.uid).ref.get();
                    if (userDoc.exists) {
                        this.user = { $key: userDoc.id, ...userDoc.data() };
                    }
                } catch (e) {
                    console.warn('No se pudo leer user subdoc:', e);
                }
            }

            this.isAccountAvailable.next(true);
        }, (err: any) => {
            console.error('Error snapshot account:', err);
            this.isAccountAvailable.next(false);
        });
    }

    async registerDevice(code: any): Promise<void> {

        try {
            const anonUser: any = await this.angularAuth.signInAnonymously();

            const deviceId = anonUser.user.uid;

            const snapshots = await this.db.collection('devices_abc').ref
                .where('code', '==', code)
                .get();

            if (snapshots.empty) {
                return await this.logoutAndReject("Device not found");
            }

            const element = snapshots.docs[0];
            const device: any = { ...(element.data() || {}), $key: element.id };

            if (!device.available) {
                return await this.logoutAndReject("Device not available");
            }

            await this.saveDevicePreferences(device);
            await this.updateDeviceInFirestore(device, deviceId);

            this.authenticatedSubject.next(true);
            await this.loadToken();

        } catch (error) {
            console.error("Error in registerDevice:", error);
            await this.logoutAndReject("Unexpected error");
        }
    }

    async loginAdminUser(email: any, password: any) {
        try {
            const cred = await this.angularAuth.signInWithEmailAndPassword(email, password);
            const uid = cred.user?.uid;
            if (!uid) throw new Error('UID inválido');
            this.uid = uid;

            await Preferences.set({ key: 'type', value: 'admin' });
            await Preferences.remove({ key: 'account_key' });
            await Preferences.remove({ key: 'device_key' });
            await Preferences.remove({ key: 'location_key' });
            await Preferences.remove({ key: 'area_key' });

            // Cargar datos de la cuenta
            this.authenticatedSubject.next(true);
            await this.router.navigateByUrl('admin/accounts', { replaceUrl: true });
            return true;
        } catch (error) {
            console.error('Error logging in admin:', error);
            this.authenticatedSubject.next(false);
            return false;
        }
    }

    async checkDevice() {
        try {
            const snapshot: any = await this.db.collection(`accounts_abc/${this.account_key}/devices`).ref.doc(this.device_key).get();
            this.device = { ...snapshot.data(), $key: snapshot.id };
        } catch (err) {
            console.error(err);
        }
    }

    async checkLocation() {
        try {
            const locationSnapshot: any = await this.db.collection(`accounts_abc/${this.account_key}/locations`).ref.doc(this.location_key).get();
            this.location = { ...locationSnapshot.data(), $key: locationSnapshot.id };
        } catch (error) {
            console.error(error);
        }
    }

    async checkArea() {
        try {
            const areaSnapshot: any = await this.db.collection(`accounts_abc/${this.account_key}/areas`).ref.doc(this.area_key).get();
            this.area = { ...areaSnapshot.data(), $key: areaSnapshot.id };
        } catch (err) {
            console.error(err);
        }
    }

    async checkUser() {
        try {
            const userSnapshot: any = await this.db.collection(`users`).ref.doc(this.uid).get();
            this.user = { ...userSnapshot.data(), $key: userSnapshot.id };

        } catch (error) {
            console.error(error);
        }
    }

    private async saveDevicePreferences(device: any): Promise<void> {
        const preferences = [
            { key: 'type', value: 'abc' },
            { key: 'account_key', value: device.account_key },
            { key: 'device_key', value: device.$key },
            { key: 'location_key', value: device.location_key },
            { key: 'area_key', value: device.area_key },
        ];

        await Promise.all(preferences.map(pref => Preferences.set(pref)));
    }

    private async updateDeviceInFirestore(device: any, uid: string): Promise<void> {
        const device_id = await Device.getId();
        const batch = this.db.firestore.batch();

        batch.update(this.db.firestore.collection(`devices_abc`).doc(device.$key), {
            available: false,
            device_id: device_id.identifier,
            updated_at: serverTimestamp(),
            activeUID: uid
        });

        batch.update(this.db.firestore.collection(`accounts_abc/${device.account_key}/devices`).doc(device.$key), {
            available: false,
            device_id: device_id.identifier,
            updated_at: serverTimestamp()
        });

        await batch.commit();
    }

    private async logoutAndReject(message: string): Promise<void> {
        const user = await this.angularAuth.currentUser;
        if (user && user.isAnonymous) {
            await user.delete();
            await this.angularAuth.signOut();
        }
        this.authenticatedSubject.next(false);
        throw new Error(message);
    }

    logout() {
        this.angularAuth.signOut().then(() => {
            window.location.reload();
        })
    }

    async lockUser() {
        const modals = Array.from(document.getElementsByTagName("ion-modal"));
        const popovers = Array.from(document.getElementsByTagName("ion-popover"));
        const alerts = Array.from(document.getElementsByTagName("ion-alert"));

        for (const modal of modals) await modal.dismiss();
        for (const popover of popovers) await popover.dismiss();
        for (const alert of alerts) await alert.dismiss();

        Preferences.remove({ key: 'uid' }).then(data => {
            this.uid = '';
            this.router.navigate(['/code'], { replaceUrl: true, clearHistory: true, } as NavigationExtras);
        })
    }

    async logOut(opts?: { skipNavigate?: boolean }): Promise<void> {

        try {
            // 1) Limpiar prefs en paralelo
            await Promise.all([
                Preferences.remove({ key: 'type' }),
                Preferences.remove({ key: 'account' }),
                Preferences.remove({ key: 'branch' }),
                Preferences.remove({ key: 'device' }),
                Preferences.remove({ key: 'device_name' }),
                Preferences.remove({ key: 'device_code' }),
            ]);

            this.authenticatedSubject.next(false);

            // 2) Liberar device SOLO si tenés claves definidas y aplica a tu modo actual
            const batch = this.db.firestore.batch();

            if (this.device_key) {
                batch.update(this.db.firestore.collection('devices').doc(this.device_key), {
                    available: true,
                    device_id: '',
                    activeUID: deleteField()
                });

            }

            if (this.account_key && this.device_key) {
                batch.update(
                    this.db.firestore
                        .collection(`accounts_abc/${this.account_key}/abc/main/locations`)
                        .doc(this.device_key),
                    { available: true }
                );
            }

            // Si no había nada que actualizar, Firestore falla un commit vacío.
            // Solo comiteá si agregaste updates:
            // (truco simple: llevá un flag o contá con try/catch y omite commit si no hubo update)
            try {
                // Si no hubo updates, este commit podría fallar: capturalo silenciosamente
                await batch.commit();
            } catch (e) {
                // No pasa nada si no había updates; log opcional:
                console.warn('Batch commit skipped or failed:', e);
            }

            // 3) Cerrar sesión Firebase
            const user = await this.angularAuth.currentUser;

            if (user?.isAnonymous) {
                try { await user.delete(); } catch (e) { console.error('No se pudo eliminar anónimo:', e); }
            }

            await this.angularAuth.signOut();

        } catch (err) {
            console.error('Error en logOut():', err);
            // Aun con error, intentá llevar al login
        } finally {
            // 4) Navegá SIEMPRE al login
            if (!opts?.skipNavigate) {
                this.router.navigate(['/login'], { replaceUrl: true } as any);
            }
        }
    }

    deleteAnonymousUser(value: any, device_key: any) {
        return new Promise((resolve, reject) => {
            let batch = this.db.firestore.batch();

            if (value) {
                this.db.collection('devices_abc').doc(device_key)
                    .get()
                    .toPromise()
                    .then((snapshot: any) => {
                        let device = snapshot.data();
                        device.$key = snapshot.id;

                        if (device.activeUID) {
                            let postData = {
                                device: device_key,
                                account: this.account,
                                admin: this.uid,
                                uid: device.activeUID
                            }

                            this.http.post(`${environment.api}/remove_anonymous`, postData).subscribe(response => {

                                batch.update(this.db.firestore.collection(`accounts_abc/${this.account.$key}/devices`).doc(device_key), {
                                    available: value,
                                    device_id: '',
                                    activeUID: deleteField()
                                });

                                batch.update(this.db.firestore.collection(`devices_abc`).doc(device_key), {
                                    available: value,
                                    device_id: '',
                                    activeUID: deleteField()
                                });

                                batch.commit().then(data => {
                                    resolve(true);
                                }, err => {
                                    reject();
                                });

                            }, err => {
                                batch.update(this.db.firestore.collection(`accounts_abc/${this.account.$key}/devices`).doc(device_key), {
                                    available: value,
                                    device_id: '',
                                    activeUID: deleteField()
                                });

                                batch.update(this.db.firestore.collection(`devices_abc`).doc(device_key), {
                                    available: value,
                                    device_id: '',
                                    activeUID: deleteField()
                                });

                                batch.commit().then(data => {
                                    resolve(true);
                                }, err => {
                                    reject();
                                });
                            });
                        } else {
                            batch.update(this.db.firestore.collection(`accounts_abc/${this.account.$key}/devices`).doc(device_key), {
                                available: value,
                                device_id: '',
                                activeUID: deleteField()
                            });

                            batch.update(this.db.firestore.collection(`devices_abc`).doc(device_key), {
                                available: value,
                                device_id: '',
                                activeUID: deleteField()
                            });

                            batch.commit().then(data => {
                                resolve(true);
                            }, err => {
                                reject();
                            });
                        }
                    })


            } else {
                batch.update(this.db.firestore.collection(`accounts_abc/${this.account.$key}/devices`).doc(device_key), {
                    available: value,
                    device_id: ''
                });

                batch.update(this.db.firestore.collection(`devices_abc`).doc(device_key), {
                    available: value,
                    device_id: ''
                });

                batch.commit().then(data => {
                    resolve(true);
                }, err => {
                    reject();
                });
            }
        });
    }

    checkCode(code: any, message: string = 'Ingresando a Quanto POS...') {
        return new Promise((resolve, reject) => {
            this.components.showLoader(message, 'dark').then(() => {
                let real_code = code.slice(0, 4);
                this.db.collection(`accounts_abc/${this.account_key}/users`).ref
                    .where('code', '==', real_code)
                    .get()
                    .then((snapshots: any) => {

                        if (snapshots.empty) {
                            reject(false);
                        } else {
                            snapshots.forEach((element: any) => {
                                this.user = element.data();
                                this.user.$key = element.id;
                                this.uid = element.id;
                                this.logged_token = true;
                                resolve(true);
                                this.inventoryCountingListener();
                            });
                        }
                    }, err => {
                        console.error(err);
                        reject();
                    });
            })
        });
    }

    async checkUserCode(code: string): Promise<{ user: any } | false> {
        try {
            const real_code = code.slice(0, 4);
            const snapshots: any = await this.db
                .collection(`accounts_abc/${this.account_key}/users`)
                .ref.where('code', '==', real_code)
                .get();

            if (snapshots.empty) return false;

            let userData: any = null;
            snapshots.forEach((element: any) => {
                userData = element.data();
                userData.$key = element.id;
            });

            return { user: userData };
        } catch (err) {
            console.error(err);
            return false;
        }
    }

    async createAccount(form_data: any): Promise<string> {
        try {
            const user_db: any = await this.angularAuth.createUserWithEmailAndPassword(form_data.email, form_data.password);
            this.uid = user_db.user?.uid;
            if (!this.uid) throw new Error('No se recibió UID del usuario');

            await this.checkUser();
            this.authenticatedSubject.next(true);

            this.user = {
                name: form_data.user_name,
                lastname: form_data.user_lastname,
                email: form_data.email,
            };

            const account_id = await this.setupNewAccount(form_data);
            this.setAdminContext(account_id);

            return account_id;
        } catch (err) {
            this.authenticatedSubject.next(false);
            throw err;
        }
    }

    async setupNewAccount(account_data: any): Promise<any> {

        try {
            const batch = this.db.firestore.batch();

            const accountRef = this.db.firestore.collection('accounts_abc').doc();

            batch.set(accountRef, {
                name: account_data.name,
                phone: account_data.phone,
                country: account_data.country,
                full_country: account_data.full_country,
                email: account_data.email,
                created_at: serverTimestamp(),
                updated_at: serverTimestamp(),
                owner: this.uid,
                active: true,
                admins: [this.uid],
                currency: {
                    symbol: '$'
                }
            });

            const userRef = this.db.firestore.collection(`accounts_abc/${accountRef.id}/users`).doc(this.uid);

            let date = new Date();
            let renewal_date = new Date(date.setMonth(date.getMonth() + 1));
            const code = Math.floor(1000 + Math.random() * 9000);

            batch.set(this.db.firestore.collection(`users`).doc(this.uid), {
                email: account_data.email,
                name: account_data.user_name,
                lastname: account_data.user_lastname,
                phone: account_data.phone,
                creation_date: serverTimestamp(),
            });
            batch.set(userRef, {
                uid: this.uid,
                name: this.user.name,
                lastname: this.user.lastname,
                email: this.user.email,
                type: 'owner',
                roles: ['owner'],
                created_at: serverTimestamp(),
                updated_at: serverTimestamp(),
                active: true,
                renewal_date: renewal_date,
                code: String(code)
            });

            await batch.commit();

            return accountRef.id;

        } catch (err) {
            console.error(err);
            throw err;
        }
    }


    private inventoryCountingListener() {
        const ref = this.db.collection(
            `accounts_abc/${this.account_key}/inventory_counting`,
            (ref) =>
                ref
                    .where('area_key', '==', this.area_key)
                    .where('active', '==', true)
                    .limit(1)
        );

        const sub = ref.snapshotChanges().subscribe((snapshot) => {
            const hasActiveCount = snapshot.length === 1;

            const countingId = hasActiveCount ? snapshot[0].payload.doc.id : null;
            this._countState$.next({
                hasActiveCount,
                counting_key: countingId,
            });

            const currentUrl = this.router.url.split('?')[0];
            const countingUrl = '/abc/inventory-counting/init-counting';

            if (hasActiveCount && currentUrl !== countingUrl) {
                this.router.navigateByUrl(countingUrl);
            } else if (!hasActiveCount && currentUrl === countingUrl) {
                this.router.navigateByUrl('/abc/inventory-counting/counting-summary');
            }
        });

        this.destroy$.pipe(take(1)).subscribe(() => sub.unsubscribe());
    }


    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get countState(): any {
        return this._countState$.value;
    }

    startCounting(key: string) {
        this._countState$.next({ hasActiveCount: true, counting_key: key });
    }

    stopCounting() {
        this._countState$.next({ hasActiveCount: false, counting_key: null });
    }
}
